import { XIcon } from "lucide-react";
import React from "react";
import { useObjectRef } from "react-aria";
import {
  Dialog as RACDialog,
  type DialogProps as RACDialogProps,
  Heading as RACHeading,
  type HeadingProps as RACHeadingProps,
} from "react-aria-components";

import { cn, composeTailwindRenderProps } from "@/components/ui/utils";
import { Button, type ButtonProps } from "./button";

export { DialogTrigger } from "react-aria-components";

type DialogProps = RACDialogProps & {
  hideCloseButton?: boolean;
};

export function Dialog({
  className,
  children,
  hideCloseButton,
  ...props
}: DialogProps) {
  return (
    <RACDialog
      {...props}
      className={cn(
        "relative flex flex-col overflow-hidden outline-hidden",
        "max-h-[inherit]",
        className,
      )}
    >
      {(renderProps) => (
        <>
          {!hideCloseButton && <DialogCloseButton />}
          {typeof children === "function" ? children(renderProps) : children}
        </>
      )}
    </RACDialog>
  );
}

type DialogHeaderProps = React.HTMLAttributes<HTMLDivElement> & {
  ref?: React.RefObject<HTMLDivElement>;
};

export function DialogHeader({ className, ref, ...props }: DialogHeaderProps) {
  const headerRef = useObjectRef(ref);

  React.useEffect(() => {
    const header = headerRef.current;
    if (!header) {
      return;
    }

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        header.parentElement?.style.setProperty(
          "--dialog-header-height",
          `${entry.target.clientHeight}px`,
        );
      }
    });

    observer.observe(header);

    return () => {
      observer.unobserve(header);
    };
  }, [headerRef.current]);

  return (
    <div
      ref={headerRef}
      className={cn(
        "flex flex-col space-y-1.5 text-center sm:text-left px-6 py-4",
        className,
      )}
      {...props}
    />
  );
}

type DialogTitleProps = RACHeadingProps & {
  ref?: React.RefObject<HTMLHeadingElement>;
};

export function DialogTitle({
  className,
  ref,
  level = 2,
  ...props
}: DialogTitleProps) {
  return (
    <RACHeading
      {...props}
      ref={ref}
      className={cn("text-lg font-semibold tracking-tight", className)}
      slot="title"
      level={level}
    />
  );
}

type DialogBodyProps = React.HTMLAttributes<HTMLDivElement> & {
  ref?: React.RefObject<HTMLDivElement>;
};

export function DialogBody({ className, ref, ...props }: DialogBodyProps) {
  return (
    <div
      {...props}
      ref={ref}
      className={cn(
        "flex flex-1 flex-col gap-2 overflow-auto px-6 py-1",
        "max-h-[calc(var(--visual-viewport-height)-var(--visual-viewport-vertical-padding)-var(--dialog-header-height,0px)-var(--dialog-footer-height,0px))]",
        className,
      )}
    />
  );
}

type DialogFooterProps = React.HTMLAttributes<HTMLDivElement> & {
  ref?: React.RefObject<HTMLDivElement>;
};

export function DialogFooter({ className, ref, ...props }: DialogFooterProps) {
  const footerRef = useObjectRef(ref);

  React.useEffect(() => {
    const footer = footerRef.current;

    if (!footer) {
      return;
    }

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        footer.parentElement?.style.setProperty(
          "--dialog-footer-height",
          `${entry.target.clientHeight}px`,
        );
      }
    });

    observer.observe(footer);
    return () => {
      observer.unobserve(footer);
    };
  }, [footerRef.current]);

  return (
    <div
      {...props}
      ref={footerRef}
      className={cn(
        "flex flex-col gap-2 sm:flex-row-reverse sm:justify-start sm:gap-3 px-6 py-4",
        className,
      )}
    />
  );
}

export function DialogCloseButton({
  variant = "ghost",
  ...props
}: ButtonProps) {
  if (props.children) {
    return <Button {...props} slot="close" variant={variant} />;
  }

  const { size = "icon", "aria-label": ariaLabel, ...restProps } = props;

  return (
    <Button
      {...restProps}
      slot="close"
      variant={variant}
      size={size}
      excludeFromTabOrder
      className={composeTailwindRenderProps(
        props.className,
        "absolute end-2 top-2 p-1.5 text-muted-foreground hover:text-foreground size-8",
      )}
    >
      <XIcon aria-label={ariaLabel ?? "Close"} />
    </Button>
  );
}
